class Drawer {
  constructor(id) {
    this.panel = document.getElementById(id);
    if (!this.panel) return;

    this.openers = document.querySelectorAll(`[data-drawer-open="${id}"]`);
    this.closers = this.panel.querySelectorAll('[data-drawer-close]');
    this.releaseFocus = null;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);

    this.openers.forEach((el) => el.addEventListener('click', this.open));
    this.closers.forEach((el) => el.addEventListener('click', this.close));
  }

  /**
   * Slide the drawer in, lock body scroll (keeping the scroll position) and move focus into it.
   * Escape closes it and Tab stays inside it (see manageDialogFocus in global.js).
   */
  open() {
    if (this.releaseFocus) return;

    this.panel.classList.add('is-open');
    this.panel.setAttribute('aria-modal', 'true');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'true'));
    lockBodyScroll();
    this.releaseFocus = manageDialogFocus(this.panel, { onEscape: () => this.close() });
  }

  /**
   * Close the drawer, release the scroll lock and return focus to the element that opened it.
   * @param {{ restoreFocus?: boolean }} [options] - Pass restoreFocus:false when the trigger is gone
   *   (e.g. the viewport just switched to desktop, where the mobile triggers are hidden).
   */
  close(options = {}) {
    if (!this.releaseFocus) return;

    this.panel.classList.remove('is-open');
    this.panel.removeAttribute('aria-modal');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    unlockBodyScroll();

    this.releaseFocus({ restoreFocus: options.restoreFocus !== false });
    this.releaseFocus = null;
  }

  /**
   * Apply the next state change without the slide animation. Crossing the lg breakpoint swaps the panel
   * between its static desktop layout and the off-canvas transform; with the transition live, that swap
   * plays as a visible slide across the screen.
   */
  skipTransition() {
    if (!this.panel) return;

    this.panel.classList.add('drawer-panel--instant');
    void this.panel.offsetWidth;
    requestAnimationFrame(() => this.panel.classList.remove('drawer-panel--instant'));
  }
}

/**
 * A panel anchored below a trigger: the desktop cart popover (hover/focus) and the profile dropdown (click).
 * Replaces Flowbite's Popover/Dropdown. The panel is placed with fixed positioning from the trigger's rect and
 * follows it on scroll/resize while open. It closes on Escape, on an outside click, when the trigger is no
 * longer rendered (below lg), and in hover mode shortly after the pointer/focus leaves trigger and panel.
 * Open/closed state is the panel's hidden classes, so other scripts (cart.js) can share it.
 */
class AnchoredPanel {
  /**
   * @param {object} options
   * @param {HTMLElement} options.trigger - Element the panel is anchored to.
   * @param {HTMLElement} options.panel - Panel to show/hide.
   * @param {string[]} options.hiddenClasses - Classes that hide the panel; the first one marks "closed".
   * @param {'hover'|'click'} [options.mode='click'] - How the trigger opens the panel.
   * @param {'center'|'end'} [options.align='center'] - Horizontal alignment to the trigger.
   * @param {number} [options.offset=10] - Gap between trigger and panel, in px.
   */
  constructor({ trigger, panel, hiddenClasses, mode = 'click', align = 'center', offset = 10 }) {
    this.trigger = trigger;
    this.panel = panel;
    this.hiddenClasses = hiddenClasses;
    this.mode = mode;
    this.align = align;
    this.offset = offset;
    this.hideTimer = null;
    this.frame = null;
    this.isListening = false;

    this.onViewportChange = this.onViewportChange.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    // Fixed up front so revealing the panel never puts it into the trigger's layout (e.g. the header's flex
    // row), which would shift the trigger before it is measured.
    Object.assign(panel.style, { position: 'fixed', top: '0px', left: '0px', right: 'auto', margin: '0' });

    if (mode === 'hover') {
      [trigger, panel].forEach((el) => {
        el.addEventListener('mouseenter', () => this.show());
        el.addEventListener('mouseleave', () => this.scheduleHide());
        el.addEventListener('focusin', () => this.show());
        el.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.scheduleHide();
        });
      });
    } else {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen() ? this.hide() : this.show();
      });
    }
  }

  contains(node) {
    return !!node && (this.trigger.contains(node) || this.panel.contains(node));
  }

  isOpen() {
    return !this.panel.classList.contains(this.hiddenClasses[0]);
  }

  show() {
    clearTimeout(this.hideTimer);
    this.panel.classList.remove(...this.hiddenClasses);
    if (this.mode === 'click') this.trigger.setAttribute('aria-expanded', 'true');
    this.position();

    if (this.isListening) return;
    this.isListening = true;
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange, { passive: true });
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeydown);
  }

  hide() {
    clearTimeout(this.hideTimer);
    this.panel.classList.add(...this.hiddenClasses);
    if (this.mode === 'click') this.trigger.setAttribute('aria-expanded', 'false');

    if (!this.isListening) return;
    this.isListening = false;
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeydown);
  }

  // Same grace period as Flowbite (100ms) so the pointer can travel from the trigger into the panel.
  scheduleHide() {
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      const isHovered = this.trigger.matches(':hover') || this.panel.matches(':hover');
      if (!isHovered && !this.contains(document.activeElement)) this.hide();
    }, 100);
  }

  position() {
    const rect = this.trigger.getBoundingClientRect();

    // Trigger not rendered (e.g. the viewport dropped below lg): nothing to anchor to.
    if (rect.width === 0 && rect.height === 0) {
      this.hide();
      return;
    }

    const width = this.panel.offsetWidth;
    const edge = 8;
    const preferredLeft = this.align === 'end' ? rect.right - width : rect.left + rect.width / 2 - width / 2;
    const left = Math.max(edge, Math.min(preferredLeft, window.innerWidth - width - edge));

    this.panel.style.top = `${rect.bottom + this.offset}px`;
    this.panel.style.left = `${left}px`;
  }

  onViewportChange() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (this.isOpen()) this.position();
    });
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.hide();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;

    const hadFocus = this.contains(document.activeElement);
    this.hide();
    if (hadFocus) this.trigger.focus();
  }
}

class HeaderController {
  constructor() {
    this.elements = this.initializeElements();
    this.config = {
      // The drawer, mobile cart and mobile nav layout run below lg (see .drawer-panel in style.css).
      mobileBreakpoint: MEDIA_QUERY.BELOW_LG,
      stickyOffset: 0,
    };

    this.init();
  }

  /**
   * Initialize and cache DOM elements
   * Returns an object with references to key elements
   */
  initializeElements() {
    return {
      header: document.querySelector('#shopify-section-header header'),
      headerSection: document.getElementById('shopify-section-header'),
      announcementBar: document.getElementById('announcement-bar'),
      dismissAnnouncementBtn: document.getElementById('dismissAnnouncement'),
      profileDropdown: document.getElementById('profileDropdown'),
      profileDropdownToggle: document.querySelector('[data-profile-dropdown-toggle]'),
      cartPopup: document.getElementById('cart-popup'),
      cartPopupTrigger: document.querySelector('[data-cart-popup-trigger]'),
      logoutLinks: document.querySelectorAll('a[href^="/account/logout"]'),
      accountLinkBlocks: document.querySelectorAll('.header-profile-link-block .account-links'),
      submenuItems: document.querySelectorAll('.submenu-item'),
      mobileStickyBar: document.getElementById('mobileStickyBar'),
    };
  }

  /**
   * Initialize all header functionality
   * Binds events and sets up initial state
   */
  init() {
    this.setupScrollBehavior();
    this.setupAnnouncementBar();
    this.setupDrawers();
    this.setupAccountLinks();
    this.setupCartPopover();
    this.setupProfileDropdown();
    this.setupLogoutHandlers();
    this.setupMobileSubmenus();
    this.setupDesktopSubmenuState();
    this.setupMobileStickyBar();
  }

  setupDrawers() {
    this.navDrawer = new Drawer('navbarCollapse');
    this.cartDrawer = new Drawer('mobile-popup-cart-item');

    // The drawers and their close buttons only exist below lg. If the viewport grows past it while one is
    // open (tablet rotation, window resize) close it, or the scroll lock would stay with no way to undo it.
    // Either direction, the breakpoint swap must not animate (see Drawer.skipTransition).
    watchMediaQuery(this.config.mobileBreakpoint, (isMobile) => {
      this.navDrawer.skipTransition?.();
      this.cartDrawer.skipTransition?.();

      if (isMobile) return;

      this.navDrawer.close?.({ restoreFocus: false });
      this.cartDrawer.close?.({ restoreFocus: false });
    });
  }

  /**
   * Setup scroll behavior and sticky header
   * Disables automatic scroll restoration and manages sticky header class
   */
  setupScrollBehavior() {
    // Disable automatic scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    this.cacheStickyThreshold();

    let ticking = false;

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.handleStickyHeader();
          ticking = false;
        });
      },
      { passive: true }
    );

    // The threshold depends on layout, so recompute it when layout can change.
    ['resize', 'orientationchange'].forEach((evt) => {
      window.addEventListener(evt, () => this.cacheStickyThreshold());
    });

    const remeasure = () => {
      this.cacheStickyThreshold();
      this.handleStickyHeader();
    };

    if (document.readyState === 'complete') remeasure();
    else window.addEventListener('load', remeasure, { once: true });

    // Initial sticky header setup
    this.handleStickyHeader();
  }

  /**
   * Cache the scroll offset at which the header pins. The announcement bar is its own
   * section rendered above #shopify-section-header, so it scrolls out naturally and the
   * header pins at top: 0 once the page has scrolled past the bar's height.
   */
  cacheStickyThreshold() {
    const { announcementBar } = this.elements;

    this.stickyThreshold = announcementBar ? announcementBar.offsetHeight : 0;
  }

  /**
   * Handle sticky header on scroll
   * Adds or removes sticky class based on scroll position
   */
  handleStickyHeader() {
    const { header } = this.elements;

    if (!header) return;

    const y = window.pageYOffset;
    const threshold = this.stickyThreshold || 0;
    const isSticky = header.classList.contains('sticky-header');

    let shouldBeSticky = isSticky;

    if (!isSticky && y > threshold + 4) shouldBeSticky = true;
    else if (isSticky && y <= Math.max(threshold - 4, 0)) shouldBeSticky = false;

    // Only touch the DOM when the state actually changes.
    if (shouldBeSticky === isSticky) return;

    header.classList.toggle('sticky-header', shouldBeSticky);
  }

  /**
   * Setup announcement bar dismissal
   * Hides the bar and remembers the dismissed message so it stays hidden
   * until the configured announcement text changes
   */
  setupAnnouncementBar() {
    const { announcementBar, dismissAnnouncementBtn } = this.elements;

    if (!announcementBar || !dismissAnnouncementBtn) return;

    dismissAnnouncementBtn.addEventListener('click', () => {
      localStorage.setItem('dismissed-announcement-key', announcementBar.dataset.announcementKey);
      announcementBar.style.display = 'none';

      // The bar no longer occupies space, so recompute the pin offset (now 0) and state.
      this.cacheStickyThreshold();
      this.handleStickyHeader();
    });
  }

  /**
   * Setup account link behaviors
   * Handles navigation and active states for account links
   */
  setupAccountLinks() {
    this.elements.accountLinkBlocks?.forEach((link) => {
      link.addEventListener('click', (event) => this.handleAccountLinkClick(event, link));

      // Set active link based on current hash
      const hash = toHash(link.getAttribute('href'));

      if (hash && hash === window.location.hash) {
        link.classList.add('active-link');
      }
    });
  }

  /**
   * Handle account link click
   * Navigates to account section and updates active states
   */
  handleAccountLinkClick(event, clickedLink) {
    event.preventDefault();

    const hash = toHash(clickedLink.getAttribute('href'));
    if (!hash) return;

    const redirectUrl = `${location.origin}${PAGE_URLS.ACCOUNT}${hash}`;

    // If already on account page, just change hash and reload
    if (toPathname(location.pathname) === toPathname(PAGE_URLS.ACCOUNT)) {
      location.hash = hash;
      location.reload();
    } else {
      // Navigate to account page with hash
      location.href = redirectUrl;
    }

    // Update active states
    this.elements.accountLinkBlocks.forEach((item) => item.classList.remove('active-link'));
    clickedLink.classList.add('active-link');
  }

  /**
   * Setup profile dropdown handlers
   * Binds click events to dropdown items to close the dropdown
   */
  setupProfileDropdown() {
    const { profileDropdown, profileDropdownToggle } = this.elements;
    if (!profileDropdown || !profileDropdownToggle) return;

    // Right-aligned under the avatar button (Flowbite used a -100px skidding for the same placement).
    this.profileDropdown = new AnchoredPanel({
      trigger: profileDropdownToggle,
      panel: profileDropdown,
      hiddenClasses: ['hidden'],
      mode: 'click',
      align: 'end',
    });

    profileDropdown.querySelectorAll('.profile-dropdown-item').forEach((item) => {
      item.addEventListener('click', () => this.profileDropdown.hide());
    });
  }

  /**
   * Desktop cart popover: opens on hover/focus of the header cart link. Exposed as window.cartPopover so
   * cart.js can open it after add-to-cart and close it from the popup's close button.
   */
  setupCartPopover() {
    const { cartPopup, cartPopupTrigger } = this.elements;
    if (!cartPopup || !cartPopupTrigger) return;

    this.cartPopover = new AnchoredPanel({
      trigger: cartPopupTrigger,
      panel: cartPopup,
      hiddenClasses: ['invisible', 'opacity-0'],
      mode: 'hover',
      align: 'center',
    });

    window.cartPopover = this.cartPopover;
  }

  /**
   * Setup logout handlers
   * Binds click events to logout links
   */
  setupLogoutHandlers() {
    this.elements.logoutLinks.forEach((link) => {
      link.addEventListener('click', (event) => this.handleLogout(event));
    });
  }

  /**
   * Handle logout process
   * Performs logout API call and redirects to login page
   */
  async handleLogout(event) {
    event.preventDefault();
    const exceptionMessage = window.errorMessage;
    let isRedirecting = false;

    try {
      toggleFullScreenPreloader(true);

      // Call logout API
      await fetchData(SHOPIFY_API.CUSTOMER_LOGOUT);

      // Clear session data
      clearAuthFlowState();
      deleteSessionStorageItem(SESSION_KEY.PROFILE);
      deleteSessionStorageItem(SESSION_KEY.CHECKOUT);
      deleteSessionStorageItem(SESSION_KEY.RETURN_TO);

      // Set redirecting flag and redirect to login page (keep loader active during redirect)
      isRedirecting = true;
      window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
    } catch (err) {
      showToastMessage(exceptionMessage.somethingWentWrong);
      console.error('Logout Error:', err);
    } finally {
      // Only remove loading state if we're not redirecting
      if (!isRedirecting) {
        toggleFullScreenPreloader(false);
      }
    }
  }

  /**
   * Setup mobile submenu toggles
   * Binds click events to submenu items for toggling
   */
  setupMobileSubmenus() {
    // Always bind and check the viewport at tap time: binding only when the page loads narrow meant
    // submenus stopped working after rotating or resizing across the breakpoint.
    this.elements.submenuItems.forEach((el) => {
      const toggle = el.querySelector('[data-submenu-toggle]');

      toggle?.addEventListener('click', (event) => this.handleSubmenuToggle(event, el, toggle));
    });
  }

  /**
   * Keep aria-expanded in sync on desktop, where CSS opens submenus on hover / focus-within
   * (style.css "Submenu Hover"). Visual behavior is unchanged; screen readers now hear the state.
   */
  setupDesktopSubmenuState() {
    this.elements.submenuItems.forEach((el) => {
      const toggle = el.querySelector('[data-submenu-toggle]');
      if (!toggle) return;

      const sync = () => {
        if (this.isMobile()) return;
        const isOpen = el.matches(':hover') || el.contains(document.activeElement);
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      };

      el.addEventListener('mouseenter', sync);
      el.addEventListener('mouseleave', sync);
      el.addEventListener('focusin', sync);
      el.addEventListener('focusout', () => requestAnimationFrame(sync));
    });
  }

  /**
   * Handle submenu toggle in the mobile drawer.
   * From lg the submenu opens on hover / focus-within in CSS, so taps there are left alone.
   * @param {MouseEvent} event
   * @param {HTMLElement} submenuItem - The .submenu-item list item.
   * @param {HTMLButtonElement} toggle - The parent menu button.
   */
  handleSubmenuToggle(event, submenuItem, toggle) {
    if (!this.isMobile()) return;

    event.preventDefault();

    const isExpanded = submenuItem.classList.toggle('submenu-item-active');
    toggle.classList.toggle('active', isExpanded);
    toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    toggle.querySelector('.caret-icon svg')?.classList.toggle('active', isExpanded);
    submenuItem.querySelector('.submenu')?.classList.toggle('hidden', !isExpanded);
  }

  /**
   * Setup the mobile sticky CTA bar.
   * The bar lives in the gap between the hero and the footer: it appears once the hero
   * scrolls out of view and hides again as the footer scrolls in, so it never covers
   * the hero or sits on top of the footer. Mobile-only (lg:hidden), so it stays
   * inert on desktop even while observing.
   */
  setupMobileStickyBar() {
    const { mobileStickyBar } = this.elements;
    if (!mobileStickyBar) return;

    const hero = this.resolveHeroElement();
    if (!hero) return;

    const footer = document.getElementById('shopify-section-footer');
    // Hide the bar just before the footer would slide under it, by its own height.
    const footerMargin = mobileStickyBar.offsetHeight || 72;

    this.pastHero = false;
    this.footerInView = false;

    if ('IntersectionObserver' in window) {
      this.heroObserver = new IntersectionObserver(
        (entries) => {
          this.pastHero = !entries[entries.length - 1].isIntersecting;
          this.updateMobileStickyBar();
        },
        { threshold: 0 }
      );
      this.heroObserver.observe(hero);

      if (footer) {
        this.footerObserver = new IntersectionObserver(
          (entries) => {
            this.footerInView = entries[entries.length - 1].isIntersecting;
            this.updateMobileStickyBar();
          },
          { threshold: 0, rootMargin: `0px 0px ${footerMargin}px 0px` }
        );
        this.footerObserver.observe(footer);
      }
    } else {
      const onScroll = () => {
        this.pastHero = hero.getBoundingClientRect().bottom <= 0;
        this.footerInView = footer ? footer.getBoundingClientRect().top <= window.innerHeight + footerMargin : false;
        this.updateMobileStickyBar();
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /**
   * Reveal the mobile sticky bar only while the visitor is between the hero and the
   * footer — after the hero has left the viewport and before the footer enters it.
   */
  updateMobileStickyBar() {
    this.toggleMobileStickyBar(this.pastHero && !this.footerInView);
  }

  /**
   * Resolve the hero element used as the sticky-bar trigger.
   * Prefers an explicit [data-hero] marker, then falls back to the first
   * section rendered inside <main>.
   * Returns the hero Element, or null when none can be resolved.
   */
  resolveHeroElement() {
    return (
      document.querySelector('[data-hero]') ||
      document.querySelector('main > .shopify-section') ||
      document.querySelector('main > *')
    );
  }

  /**
   * Show or hide the mobile sticky bar.
   * @param {boolean} show - true to reveal the bar
   */
  toggleMobileStickyBar(show) {
    const { mobileStickyBar } = this.elements;
    if (!mobileStickyBar) return;

    mobileStickyBar.classList.toggle('is-visible', show);
    mobileStickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  /**
   * Check if current viewport is mobile
   * Returns true if viewport matches mobile breakpoint
   */
  isMobile() {
    return window.matchMedia(this.config.mobileBreakpoint).matches;
  }
}

// Initialize header controller when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HeaderController();
});
